import { FC, SyntheticEvent, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  ListItem,
  Typography,
  Popper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LocationIcon from "../../locations/LocationIcon";
import { useGetLocationsCached } from "../../../../pages/customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { ClearIcon } from "@mui/x-date-pickers";

interface LocationSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
}

const LocationSelect: FC<LocationSelectProps> = ({
  value,
  onChange,
  disabled = false,
  size = "small",
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  const { locations } = useGetLocationsCached();
  const options = ["All locations", ...locations.map(loc => loc.name ?? "")];
  const displayValue = value ?? "All locations";

  const handleChange = (_: SyntheticEvent, newValue: string | null) => {
    if (!disabled && newValue) {
      onChange(newValue === "All locations" ? null : newValue);
    }
  };

  const handleClear = () => {
    if (!disabled) {
      onChange(null);
    }
  };

  return (
    <Autocomplete
      fullWidth
      size={size}
      options={options}
      value={displayValue}
      onChange={handleChange}
      disabled={disabled}
      popupIcon={<ExpandMore />}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);

        setHovered(null);
      }}
      disableClearable
      inputValue={open ? "" : displayValue}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          onMouseEnter={() => setHovered(option)}
          onMouseLeave={() => setHovered(null)}
          sx={{
            bgcolor: hovered === option ? "inherit" : "transparent",
            "&.Mui-focused": { bgcolor: "transparent" },
            "&[aria-selected='true']": { bgcolor: "transparent" },
          }}
        >
          <Typography>{option}</Typography>
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          placeholder="Select locations"
          variant="outlined"
          label={displayValue === "All locations" ? undefined : "Locations"}
          InputLabelProps={{
            shrink: displayValue !== "All locations",
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  pl: 0,
                }}
              >
                <LocationIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {value && !disabled && (
                  <IconButton onClick={handleClear} size={size} sx={{ p: 0 }}>
                    <ClearIcon fontSize={size} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              "& input": {
                color:
                  displayValue === "All locations"
                    ? "otherTextTertiary.main"
                    : "inherit",
              },
            },
          }}
        />
      )}
      PopperComponent={props => (
        <Popper
          {...props}
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        />
      )}
    />
  );
};

export default LocationSelect;
